import { useEffect, useState, useRef } from "react";
import "./styles.scss";

import { useNavigate, useParams } from "react-router-dom";
import api from "@helpers/api";

import { useCommunity } from "@contexts/CommunityContext";

import ManageSessionModal from "@modals/Content/ManageSession";
import ManageContentModal from "@modals/Content/ManageContent";
import ManagePlaylistModal from "@modals/Content/ManagePlaylist";
import ReorderPlaylistsModal from "@modals/Content/ReorderPlaylists";
import ReorderContentsModal from "@modals/Content/ReorderContents";


import { useScreen } from 'Screen/ScreenContext';


import Hero from "./components/Hero";
import EditionModeButton from "./components/EditionModeButton";
import ActionsRow from "./components/ActionsRow";
import Playlist from "./components/Playlist";
import SingleLesson from "./components/SingleLesson";


export default function ContentHub() {
  let { section: sectionId } = useParams();

  const navigate = useNavigate();
  const { setFullWidth, setBackgroundEffect } = useScreen();

  useEffect(() => {
    setFullWidth(true);
    return () => {
      setFullWidth(false);
      setBackgroundEffect(null);
    };
  }, [setFullWidth]);


  const [section, setSection] = useState({});

  // Inicializa o estado de edição do localStorage ou usa true como padrão
  const [editingEnabled, setEditingEnabled] = useState(() => {
    const stored = localStorage.getItem('contentHub_editingEnabled');
    return stored !== null ? JSON.parse(stored) : true;
  });

  const { loading: communityLoading } = useCommunity();

  const [loading, setLoading] = useState(true);

  const REF_configSessionModal = useRef();
  const REF_configPlaylistModal = useRef();
  const REF_configContentModal = useRef();
  const REF_reorderPlaylistsModal = useRef();
  const REF_reorderContentsModal = useRef();

  // Função para atualizar o estado e salvar no localStorage
  const handleEditingToggle = (newValue) => {
    setEditingEnabled(newValue);
    localStorage.setItem('contentHub_editingEnabled', JSON.stringify(newValue));
  };

  // Sincroniza o estado com o localStorage quando necessário
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'contentHub_editingEnabled') {
        const newValue = e.newValue !== null ? JSON.parse(e.newValue) : true;
        setEditingEnabled(newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    if (loading && !communityLoading) handleLoad();
  }, [loading, communityLoading]);

  async function handleLoad() {
    try {
      const { data } = await api.get("section/" + sectionId);
      setSection({ ...data });
      setBackgroundEffect(data?.banner?.wide || data?.banner?.condensed || null)
    } catch (e) {
      console.log(e);
    }

    setLoading(false);
  }

  // Targeted refresh after a content reorder: re-fetch only the affected
  // playlist and swap it into the section instead of reloading everything.
  async function handleReloadPlaylist(playlistId) {
    try {
      const { data } = await api.get("section/playlist/" + playlistId);
      const updated = data?.data ?? data;
      if (!updated?.id) return;

      setSection((prev) => ({
        ...prev,
        content: (prev.content ?? []).map((entry) =>
          entry && !entry.single && entry.id === updated.id
            ? { ...entry, ...updated }
            : entry
        ),
      }));
    } catch (e) {
      console.log(e);
    }
  }

  function handleConfigureSession() {
    REF_configSessionModal.current.open({ ...section }, handleLoad);
  }
  function handleConfigurePlaylist(playlist = null) {
    REF_configPlaylistModal.current.open(
      { section: section.id, community: section.community, ...playlist },
      handleLoad
    );
  }
  function handleReorderPlaylists() {
    REF_reorderPlaylistsModal.current.open({ sectionId: section.id }, handleLoad);
  }
  function handleReorderContents(playlistId) {
    REF_reorderContentsModal.current.open(
      { playlistId, sectionId: section.id },
      () => handleReloadPlaylist(playlistId)
    );
  }
  function handleOpenEditContent(content) {
    REF_configContentModal.current.open({
      ...content,
      section: sectionId,
      community: section.community
    }, handleLoad);
  }
  function handleOpenContent(playlistInfo, index) {
    if (playlistInfo.single) {
      navigate("" + playlistInfo.id);
    } else {
      navigate("" + playlistInfo.contents[index].id);
    }
  }

  const isAdmin = ['owner', 'creator'].includes(section?.role);

  // Reordering needs at least two playlists to be meaningful.
  const playlistCount = (section?.content ?? []).filter(
    (entry) => entry && !entry.single
  ).length;

  if (loading) {
    return <div className="contents-page" />;
  }

  return (
    <>
      <div className="contents-page pb-9">
        <Hero info={section} />

        <div className="playlists pb-8">

          {isAdmin && editingEnabled && (
            <ActionsRow
              handleCreatePlaylist={handleConfigurePlaylist}
              handleReorderPlaylists={handleReorderPlaylists}
              handleConfigureSession={handleConfigureSession}
              canReorderPlaylists={playlistCount > 1}
            />
          )}

          {section?.content?.map((playlist, i) => (
            <>
              {playlist?.single ? (
                <SingleLesson
                  handleOpenContent={handleOpenContent}
                  content={playlist}
                  handleOpenEdit={isAdmin && editingEnabled ? handleOpenEditContent : null}
                />
              ) : (
                <Playlist
                  course={section.id}
                  handleOpenContent={handleOpenContent}
                  handleEdit={isAdmin && editingEnabled ? handleConfigurePlaylist : null}
                  handleReorderContents={isAdmin && editingEnabled ? handleReorderContents : null}
                  handleAddContent={isAdmin && editingEnabled ? handleOpenEditContent : null}
                  handleEditContent={isAdmin && editingEnabled ? handleOpenEditContent : null}
                  content={playlist}
                  key={i}
                />

              )}
            </>
          ))}
        </div>

        {isAdmin && (
          <EditionModeButton
            editingEnabled={editingEnabled}
            onToggle={handleEditingToggle}
          />
        )}
        
      </div>

      <ManageSessionModal modalRef={REF_configSessionModal} />
      <ManagePlaylistModal modalRef={REF_configPlaylistModal} />
      <ManageContentModal modalRef={REF_configContentModal} />
      <ReorderPlaylistsModal modalRef={REF_reorderPlaylistsModal} />
      <ReorderContentsModal modalRef={REF_reorderContentsModal} />
    </>
  );
}
